import { RootFieldsProps } from "@/types/editor/RootFieldsProps";
import { useForm, usePage } from "@inertiajs/react";
import { FieldLabel, usePuck } from "@measured/puck";
import { ChangeEvent, useEffect, useState } from "react";
import FileUploader from "../../FileUploader/FileUploader";
import { Icon } from "@iconify/react/dist/iconify.js";
import AuthorsSelects from "../../AuthorsSelects";
import { SelectInputProps } from "@/types/components/SelectInputProps";
import AuthorsRootField from "./AuthorsRootField";
import { AuthorModel } from "@/types/models/AuthorModel";
import TherapeuticAreaButtons from "../../TherapeuticAreaButtons";
import { TherapeuticAreaModel } from "@/types/models/TherapeuticAreaModel";
import TherapeuticAreaButtonsRootField from "./TherapeuticAreaButtonsRootField";
import TagButtonsRootField from "./TagButtonsRootField";
import TextInput from "@/Components/TextInput";
import useUuid from "@/Hooks/useUuid";

export const RootFields: RootFieldsProps = {
    root: {
        fields: {
            title: {
                type: 'text',
                label: 'Tytuł wpisu'
            },
            readTime: {
                type: 'number',
                label: 'Czas czytania'
            },
            hero: {
                type: "custom",
                label: "Obraz",
                render: ({ field, name, onChange, value }) => {
                    const [selectedImage, setSelectedImage] = useState(value);
                    const [droppable, setDroppable] = useState<boolean>(false);

                    const { data, setData, errors } = useForm<{
                        [name: string]: string;
                    }>({
                        [name]: value != undefined ? value : '',
                    })

                    useEffect(() => {
                        if (data[name] != '' && data[name] != undefined) {
                            // @ts-expect-error
                            onChange(data[name])
                        }
                    }, [data[name]])


                    return (
                        <FieldLabel label={field.label || name}
                            icon={<Icon icon={'material-symbols:image'} width={24} height={24} className="text-accordorange" />}>
                            <FileUploader
                                data={data}
                                setData={setData}
                                fileKey='hero'
                                disk='images'
                                errors={{}}
                                icon='gg:loadbar-doc'
                                label='Dodaj okładkę'
                                acceptedFormats='.jpg,.jpeg,.png,.gif,.bmp,.tiff,.heif,.heic,.avif,.svg'
                            />
                        </FieldLabel>
                    );
                },
            },
            authorsData: {
                type: "custom",
                label: "Autorzy",
                render: ({ field, name, onChange, value }) => {
                    const { authors } = usePage<{ authors: AuthorModel[] }>().props
                    const [authorsOptions, setAuthorsOptions] = useState<SelectInputProps[]>([]);

                    useEffect(() => {
                        if (authors) {
                            const mappedOptions: SelectInputProps[] = authors.map((item: AuthorModel, i: number) => ({
                                value: item.id,
                                label: `${item.name_surname} ${item.degree ? item.degree : ''} ${item.affiliation}`,
                            }))

                            setAuthorsOptions(mappedOptions);
                        }
                    }, [authors])

                    const storedAuthors: any[] = []
                    const articleAuthors = usePuck().appState.data.root.props?.article?.authors;

                    articleAuthors?.map((a: { id: string; }, i: number) => {
                        storedAuthors.push({
                            id: useUuid(),
                            value: a.id
                        })
                    })

                    const { data, setData } = useForm<{
                        authorsData: []
                    }>({
                        authorsData: articleAuthors != undefined ? articleAuthors : []
                    })

                    useEffect(() => {
                        if (data.authorsData != undefined) {
                            onChange(data.authorsData)
                        }
                    }, [data.authorsData])

                    return (
                        <FieldLabel label={field.label || name} icon={<Icon icon={'ph:users'} width={24} height={24} className="text-accordorange" />}>
                            <AuthorsRootField
                                options={authorsOptions}
                                setData={setData}
                                dataKey='authorsData'
                                storedData={data.authorsData}
                                label="Autor"
                                errors={{}}
                            />
                        </FieldLabel>
                    );
                },
            },
            therapeuticAreasData: {
                type: "custom",
                label: "Obszary terapeutyczne",
                render: ({ field, name, onChange, value }) => {
                    const { therapeuticAreas } = usePage<{ therapeuticAreas: [] }>().props

                    const articleAreas = usePuck().appState.data.root.props?.article?.therapeutic_areas;

                    let storedAreas: string[] = [];

                    if (articleAreas && articleAreas.length > 0) {
                        storedAreas = articleAreas.map((area: { id: string; }) => area.id)
                    }


                    const { data, setData } = useForm<{
                        therapeuticAreasData: string[]
                    }>({
                        therapeuticAreasData: storedAreas != undefined ? storedAreas : []
                    })



                    useEffect(() => {
                        if (data.therapeuticAreasData != undefined) {
                            onChange(data.therapeuticAreasData)
                        }
                    }, [data.therapeuticAreasData])



                    return (
                        <FieldLabel label={field.label || name} icon={<Icon icon={'material-symbols:category'} width={24} height={24} className="text-accordorange" />}>
                            <TherapeuticAreaButtonsRootField storedData={data.therapeuticAreasData} dataKey="therapeuticAreasData" setData={setData} therapeuticAreas={therapeuticAreas} errors={{}} />
                        </FieldLabel>
                    );
                },
            },
            tagsData: {
                type: "custom",
                label: "Tagi",
                render: ({ field, name, onChange, value }) => {
                    const { tags } = usePage<{ tags: [] }>().props

                    const articleTags = usePuck().appState.data.root.props?.article?.tags;

                    let storedTags: string[] = [];

                    if (articleTags && articleTags.length > 0) {
                        storedTags = articleTags.map((tag: { id: string; }) => tag.id)
                    }

                    const { data, setData } = useForm<{
                        tagsData: string[]
                    }>({
                        tagsData: storedTags != undefined ? storedTags : []
                    })

                    useEffect(() => {
                        if (data.tagsData != undefined) {
                            onChange(data.tagsData)
                        }
                    }, [data.tagsData])

                    return (
                        <FieldLabel label={field.label || name} icon={<Icon icon={'mdi:tag-outline'} width={24} height={24} className="text-accordorange" />}>
                            <TagButtonsRootField storedData={data.tagsData} dataKey="tagsData" setData={setData} tags={tags} errors={{}} />
                        </FieldLabel>
                    );
                },
            },
            expireAt: {
                type: "custom",
                label: "Data wygaśnięcia",
                render: ({ field, name, onChange, value }) => {
                    const { tags } = usePage<{ tags: [] }>().props

                    const { data, setData } = useForm<{
                        expireAt: string
                    }>({
                        expireAt: value
                    })

                    useEffect(() => {
                        // @ts-expect-error
                        if (data[name] != '' && data[name] != undefined) {
                            // @ts-expect-error
                            onChange(data[name])
                        }
                        // @ts-expect-error
                    }, [data[name]])


                    return (
                        <FieldLabel label={field.label || name} icon={<Icon icon={'mdi:clock'} width={24} height={24} className="text-accordorange" />}>
                            <input
                                id="expireAt"
                                type="date"
                                name="expireAt"
                                value={data.expireAt}
                                className="w-full _Input-input_1qi5b_46"
                                autoComplete="bday"
                                placeholder='Data wygaśnięcia'
                                required
                                min={new Date().toISOString().split('T')[0]}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setData('expireAt', e.target.value)}
                            />
                        </FieldLabel>
                    );
                },
            },
            status: {
                type: "select",
                label: "Status publikacji",
                options: [
                    { label: "Draft", value: "0" },
                    { label: "Opublikowany", value: "1" },
                ],
            },
            excerpt: {
                type: "textarea",
                label: "Zajawka",
            },
            showOnPatient: {
                label: 'Umieść na stronie dla pacjenta',
                type: 'radio',
                options: [
                    { label: "Tak", value: "1" },
                    { label: "Nie", value: "0" },
                ]
            },
            isPinProtected: {
                label: 'Chroniony',
                type: 'radio',
                options: [
                    { label: "Tak", value: "1" },
                    { label: "Nie", value: "0" },
                ]
            },
        }
    }
}